import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';
import {
  Loader2, Plus, Edit2, Trash2, Tag, CheckCircle2, XCircle, Globe, 
  Clock, Repeat, Calendar, Cloud, Info
} from 'lucide-react';
import { Switch } from '../../components/ui/switch';

export const AdminCoupons = () => {
  const { token, hasPermission } = useAdminAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  
  // Form state - Basic
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [couponType, setCouponType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [isActive, setIsActive] = useState(true);
  
  // Form state - Duration (NEW)
  const [duration, setDuration] = useState('once');
  const [durationMonths, setDurationMonths] = useState('');
  
  // Form state - Applies to (NEW)
  const [appliesTo, setAppliesTo] = useState('all');
  
  // Legacy
  const [appliesToDomain, setAppliesToDomain] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const canEdit = hasPermission('coupons:edit');

  const fetchCoupons = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/coupons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCoupons(data.coupons);
      }
    } catch (error) {
      console.error('Coupons error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [token, backendUrl]);

  const resetForm = () => {
    setCode('');
    setDescription('');
    setCouponType('percent');
    setDiscountValue('');
    setMaxUses('');
    setIsActive(true);
    setDuration('once');
    setDurationMonths('');
    setAppliesTo('all');
    setAppliesToDomain(true);
    setEditingCoupon(null);
  };

  const openEditDialog = (coupon) => {
    setEditingCoupon(coupon);
    setCode(coupon.code);
    setDescription(coupon.description || '');
    setCouponType(coupon.coupon_type);
    setDiscountValue(coupon.discount_value.toString());
    setMaxUses(coupon.max_uses?.toString() || '');
    setIsActive(coupon.is_active);
    setDuration(coupon.duration || 'once');
    setDurationMonths(coupon.duration_months?.toString() || '');
    setAppliesTo(coupon.applies_to || 'all');
    setAppliesToDomain(coupon.applies_to_domain !== false);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!code || !discountValue) {
      toast.error('Code und Rabatt sind Pflichtfelder');
      return;
    }

    if (duration === 'months' && (!durationMonths || parseInt(durationMonths) <= 0)) {
      toast.error('Bitte gib die Anzahl der Monate an');
      return;
    }

    try {
      const couponData = {
        code: code.toUpperCase(),
        description,
        coupon_type: couponType,
        discount_value: parseFloat(discountValue),
        max_uses: maxUses ? parseInt(maxUses) : null,
        is_active: isActive,
        // New fields
        duration: duration,
        duration_months: duration === 'months' ? parseInt(durationMonths) : 0,
        applies_to: appliesTo,
        // Legacy
        applies_to_domain: appliesTo !== 'cloud_only'
      };

      let response;
      if (editingCoupon) {
        response = await fetch(`${backendUrl}/api/admin/coupons/${editingCoupon.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(couponData)
        });
      } else {
        response = await fetch(`${backendUrl}/api/admin/coupons`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(couponData)
        });
      }

      if (response.ok) {
        toast.success(editingCoupon ? 'Gutschein aktualisiert' : 'Gutschein erstellt');
        setDialogOpen(false);
        resetForm();
        fetchCoupons();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (couponId) => {
    if (!confirm('Gutschein wirklich löschen?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Gutschein gelöscht');
        fetchCoupons();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Helper to display duration
  const getDurationLabel = (coupon) => {
    switch (coupon.duration) {
      case 'once': return 'Einmalig';
      case 'recurring': return 'Wiederkehrend';
      case 'months': return `${coupon.duration_months} Monate`;
      case 'first_year': return 'Erstes Jahr';
      default: return 'Einmalig';
    }
  };

  // Helper to display applies_to
  const getAppliesToLabel = (coupon) => {
    switch (coupon.applies_to) {
      case 'all': return 'Cloud + Domains';
      case 'cloud_only': return 'Nur Cloud';
      case 'domain_only': return 'Nur Domains';
      default: return coupon.applies_to_domain === false ? 'Nur Cloud' : 'Cloud + Domains';
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-coupons">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Gutscheine</h1>
          <p className="text-slate-400 mt-1">{coupons.length} Gutscheine</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" /> Neuer Gutschein
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingCoupon ? 'Gutschein bearbeiten' : 'Neuer Gutschein'}
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Erstelle einen Rabattcode mit flexiblen Optionen.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Tag className="h-4 w-4" /> Grundeinstellungen
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Code *</Label>
                      <Input
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="z.B. HELD20"
                        className="bg-slate-700 border-slate-600 text-white uppercase"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Rabatt-Typ</Label>
                      <Select value={couponType} onValueChange={setCouponType}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="percent">Prozent (%)</SelectItem>
                          <SelectItem value="fixed">Fest (€)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Rabatt {couponType === 'percent' ? '(%)' : '(€)'} *</Label>
                      <Input
                        type="number"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        placeholder={couponType === 'percent' ? '20' : '5.00'}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Max. Verwendungen</Label>
                      <Input
                        type="number"
                        value={maxUses}
                        onChange={(e) => setMaxUses(e.target.value)}
                        placeholder="Unbegrenzt"
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Beschreibung</Label>
                    <Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="z.B. Launch-Rabatt für Neukunden"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>

                {/* Duration Settings */}
                <div className="space-y-4 pt-4 border-t border-slate-700">
                  <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Clock className="h-4 w-4" /> Rabatt-Dauer
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Wie lange gilt der Rabatt?</Label>
                      <Select value={duration} onValueChange={setDuration}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="once">Einmalig (nur erste Zahlung)</SelectItem>
                          <SelectItem value="recurring">Wiederkehrend (alle Zahlungen)</SelectItem>
                          <SelectItem value="months">Für X Monate</SelectItem>
                          <SelectItem value="first_year">Erstes Jahr (für Domains)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {duration === 'months' && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">Anzahl Monate</Label>
                        <Input
                          type="number"
                          value={durationMonths}
                          onChange={(e) => setDurationMonths(e.target.value)}
                          placeholder="z.B. 3"
                          min="1"
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-700/30 rounded-lg p-3 text-sm text-slate-400">
                    <Info className="h-4 w-4 inline mr-2" />
                    {duration === 'once' && 'Der Rabatt gilt nur für die erste Zahlung.'}
                    {duration === 'recurring' && 'Der Rabatt wird auf jede Zahlung angewendet, solange das Abo aktiv ist.'}
                    {duration === 'months' && `Der Rabatt gilt für die ersten ${durationMonths || 'X'} Monatszahlungen.`}
                    {duration === 'first_year' && 'Der Rabatt gilt für das erste Jahr einer Domain-Registrierung.'}
                  </div>
                </div>

                {/* Applies To Settings */}
                <div className="space-y-4 pt-4 border-t border-slate-700">
                  <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Cloud className="h-4 w-4" /> Anwendungsbereich
                  </h3>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Worauf wird der Rabatt angewendet?</Label>
                    <Select value={appliesTo} onValueChange={setAppliesTo}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="all">Cloud + Domains (alles)</SelectItem>
                        <SelectItem value="cloud_only">Nur Cloud-Hosting</SelectItem>
                        <SelectItem value="domain_only">Nur Domains</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="bg-slate-700/30 rounded-lg p-3 text-sm text-slate-400">
                    <Info className="h-4 w-4 inline mr-2" />
                    {appliesTo === 'all' && 'Der Rabatt wird auf Cloud-Hosting UND Domain-Kosten angewendet.'}
                    {appliesTo === 'cloud_only' && 'Der Rabatt gilt NUR für Cloud-Hosting. Domain-Kosten werden nicht rabattiert.'}
                    {appliesTo === 'domain_only' && 'Der Rabatt gilt NUR für Domain-Registrierungen und -Transfers.'}
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                  <div className="space-y-0.5">
                    <Label className="text-slate-300">Gutschein aktiv</Label>
                    <p className="text-slate-500 text-sm">Deaktivierte Gutscheine können nicht eingelöst werden</p>
                  </div>
                  <Switch
                    checked={isActive}
                    onCheckedChange={setIsActive}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600 text-slate-300">
                  Abbrechen
                </Button>
                <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700">
                  {editingCoupon ? 'Speichern' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Coupons Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : coupons.length === 0 ? (
            <p className="text-slate-500 text-center py-20">Keine Gutscheine vorhanden</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Code</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Rabatt</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Dauer</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Gilt für</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Verwendet</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                    {canEdit && (
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Aktionen</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-blue-400" />
                          <span className="text-white font-mono font-bold">{coupon.code}</span>
                        </div>
                        {coupon.description && (
                          <span className="text-slate-400 text-sm block mt-1">{coupon.description}</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-green-400 font-semibold">
                          {coupon.coupon_type === 'percent' 
                            ? `${coupon.discount_value}%`
                            : `${coupon.discount_value.toFixed(2)} €`
                          }
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <Badge variant="outline" className="border-slate-600 text-slate-300">
                          {coupon.duration === 'recurring' && <Repeat className="h-3 w-3 mr-1" />}
                          {coupon.duration === 'months' && <Clock className="h-3 w-3 mr-1" />}
                          {coupon.duration === 'first_year' && <Calendar className="h-3 w-3 mr-1" />}
                          {getDurationLabel(coupon)}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        <Badge variant="outline" className={`border-slate-600 ${
                          coupon.applies_to === 'cloud_only' ? 'text-blue-400' :
                          coupon.applies_to === 'domain_only' ? 'text-purple-400' :
                          'text-slate-300'
                        }`}>
                          {coupon.applies_to === 'cloud_only' && <Cloud className="h-3 w-3 mr-1" />}
                          {coupon.applies_to === 'domain_only' && <Globe className="h-3 w-3 mr-1" />}
                          {getAppliesToLabel(coupon)}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-slate-300">
                          {coupon.current_uses} / {coupon.max_uses || '∞'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {coupon.is_active ? (
                          <Badge className="bg-green-500/20 text-green-400">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Aktiv
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-500/20 text-slate-400">
                            <XCircle className="h-3 w-3 mr-1" /> Inaktiv
                          </Badge>
                        )}
                      </td>
                      {canEdit && (
                        <td className="py-4 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(coupon)}
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(coupon.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
